import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useState} from 'react';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ContextualMenu} from '@Components/contextual-menu';
import {VectorFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/vector-fill-color-panel';
import {CustomShadowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/custom-shadow-color-panel';
import {VectorStrokeColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/vector-stroke-color-panel';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ItemInteractivitySection} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-section';
import {ItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-shadow-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-panel/item-animation-panel';
import {LineThicknessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-thickness-option.tsx/line-thickness-option';
import {LineRoundedCornersOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-rounded-corners-option/line-rounded-corners-option';
import {LineDashOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-dash-option/line-dash-option';
import {LineCapStyleOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-cap-style-option/line-cap-style-option';
import {LineColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/LineColorOption/LineColorOption';

enum LineColorPanels {
  NONE = 'none',
  OUTLINE = 'outline',
  COLOR_FILL = 'color-fill',
  CUSTOM_SHADOW = 'custom-shadow',
  ANIMATION = 'animation',
}
export function PosterEditingLineOptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(LineColorPanels.NONE);

  if (!activeItem || (activeItem && !activeItem.isLine())) {
    throw new Error('Line Item is undefined in PosterEditingLineOptions');
  }

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedPanel !== LineColorPanels.NONE;
  };

  const getItemEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(LineColorPanels.ANIMATION);
          }}
        />
        <LineColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(LineColorPanels.COLOR_FILL);
          }}
        />
        <LineThicknessOption />
        <LineDashOption />
        <LineCapStyleOption />
        <LineRoundedCornersOption />
        <ItemShadowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(LineColorPanels.CUSTOM_SHADOW);
          }}
        />
        <ItemInteractivitySection />
      </>
    );
  };

  const getOptions = (): ReactElement<any> => {
    if (isSubPanelOpened()) {
      return getColorPanel();
    }

    switch (currentItemHeaderTab) {
      case ItemHeaderTab.EDIT:
        return getItemEditOptions();
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      default:
        throw new Error('unhandled properties for this item');
    }
  };

  const getColorPanel = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case LineColorPanels.COLOR_FILL:
        return (
          <VectorFillColorPanel
            closePanel={(): void => {
              setCurrentlyOpenedPanel(LineColorPanels.NONE);
            }}
            isAlphaDisabled={true}
            isGradientEnabled={false}
          />
        );
      case LineColorPanels.CUSTOM_SHADOW:
        return <CustomShadowColorPanel />;
      case LineColorPanels.OUTLINE:
        return <VectorStrokeColorPanel />;
      case LineColorPanels.ANIMATION:
        return <ItemAnimationPanel />;
      default:
        throw new Error(`unhandled sub panel ${currentlyOpenedPanel} for vector items`);
    }
  };

  const getPositionOptions = (): ReactElement<any> => {
    return <ItemPositionContextualOptions />;
  };

  const onBackButtonFromColorPanel = (): void => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
    }
    setCurrentlyOpenedPanel(LineColorPanels.NONE);
  };

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === LineColorPanels.ANIMATION) {
      return window.i18next.t('pmwjs_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | null => {
    if (isSubPanelOpened() || currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return (
        <ContextualMenuHeader id="line-sub-panel">
          <ContextualMenuTextHeader text={getHeaderText()} showBackButton onBackButton={onBackButtonFromColorPanel} />
        </ContextualMenuHeader>
      );
    }

    return null;
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu id="line-contextual-options" includeLeftRightPadding={!isSubPanelOpened()} header={getHeader()}>
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlyOpenedPanel]);
}
